import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Building2, Users, CalendarCog, GraduationCap, School,
  UsersRound, Search, Menu, LogOut, ScrollText, ChevronRight, BookMarked, ClipboardCheck, FileSpreadsheet, ShieldAlert, Award, Trophy, FileBarChart, ScanFace, Timer, UserX, CalendarClock, UtensilsCrossed, Soup, Package, Receipt, CalendarDays, Apple, Sparkles, ShoppingCart, Plug, AlertTriangle,
} from "lucide-react";
import { Mic, Bot } from "lucide-react";
import { AcompanhaIA } from "@/components/assistant/AcompanhaIA";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/meu-diario", label: "Meu Diário", icon: BookMarked, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/acompanhamento-diarios", label: "Acompanhamento", icon: ClipboardCheck, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/alunos", label: "Estudantes", icon: GraduationCap, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/turmas", label: "Turmas", icon: School, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/quadro-horarios", label: "Quadro de Horários", icon: CalendarClock, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/horario-professor", label: "Horário do Professor", icon: CalendarClock, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/professores", label: "Professores", icon: UsersRound, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/configuracoes-academicas", label: "Config. Acadêmica", icon: CalendarCog, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/importacao", label: "Importação", icon: FileSpreadsheet, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/disciplinar", label: "Disciplinar", icon: ShieldAlert, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/excelencia", label: "Excelência", icon: Award, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/premiacoes", label: "Premiações", icon: Trophy, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/relatorios", label: "Relatórios", icon: FileBarChart, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/presenca", label: "Presença", icon: ScanFace, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { to: "/alertas-faltas", label: "Alertas de Faltas", icon: UserX, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/pendencias-noa", label: "Pendências de NOA", icon: AlertTriangle, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/reunioes", label: "Reuniões e Atendimentos", icon: Mic, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/usuarios", label: "Usuários", icon: Users, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/escolas", label: "Escolas", icon: Building2, roles: ["SUPERADMIN", "DIRECAO"] },
  { to: "/auditoria", label: "Auditoria", icon: ScrollText, roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { to: "/automacoes", label: "Automações", icon: Timer, roles: ["SUPERADMIN"] },
  { to: "/integracoes", label: "Integrações", icon: Plug, roles: ["SUPERADMIN"] },
  { to: "/treinamento-ia", label: "Treinamento — Acompanha IA", icon: Bot, roles: ["SUPERADMIN"] },
  { to: "/merenda", label: "Merenda · Painel", icon: UtensilsCrossed, roles: ["SUPERADMIN", "DIRECAO"], end: true },
  { to: "/merenda/consumo", label: "Consumo do Dia", icon: Soup, roles: ["SUPERADMIN", "DIRECAO", "COZINHEIRA"] },
  { to: "/merenda/estoque", label: "Estoque", icon: Package, roles: ["SUPERADMIN", "DIRECAO", "COZINHEIRA"] },
  { to: "/merenda/cardapio", label: "Cardápio", icon: CalendarDays, roles: ["SUPERADMIN", "DIRECAO", "COZINHEIRA"] },
  { to: "/merenda/notas", label: "Notas Fiscais", icon: Receipt, roles: ["SUPERADMIN", "DIRECAO"] },
  { to: "/merenda/alimentos", label: "Alimentos", icon: Apple, roles: ["SUPERADMIN", "DIRECAO"] },
  { to: "/merenda/importar", label: "Importar com IA", icon: Sparkles, roles: ["SUPERADMIN", "DIRECAO"] },
  { to: "/merenda/compras", label: "Pedido de Compra", icon: ShoppingCart, roles: ["SUPERADMIN", "DIRECAO"] },
];

function SidebarLinks({ role, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => n.roles.includes(role)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.to.slice(1).replace(/\//g, "-")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
              isActive ? "bg-orange-500 text-white shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"
            }`
          }>
          <n.icon className="h-5 w-5 shrink-0" />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function GlobalSearch() {
  const { scopeParams } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef();

  useEffect(() => {
    if (!q || q.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q, ...scopeParams } });
        setResults(data.students);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q, scopeParams]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (id) => { setOpen(false); setQ(""); navigate(`/alunos/${id}`); };

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="Buscar aluno por nome, matrícula..." className="h-10 pl-9" />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          {results.map((s) => (
            <button key={s.id} data-testid={`search-result-${s.id}`} onClick={() => go(s.id)}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50">
              <Avatar className="h-8 w-8"><AvatarFallback className="bg-blue-100 text-xs text-blue-700">{s.full_name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-800">{s.full_name}</div>
                <div className="text-xs text-slate-400">Mat. {s.registration} · {s.class_name || "—"}</div>
              </div>
              <ChevronRight className="ml-auto h-4 w-4 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout, schools, selectedSchoolId, chooseSchool, isSuperadmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };
  const initials = (user?.name || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  const Sidebar = (
    <div className="flex h-full flex-col bg-slate-900 py-5">
      <div className="shrink-0 px-5 pb-6"><BrandLogo light /></div>
      <div className="sidebar-scroll min-h-0 flex-1 overflow-y-auto">
        <SidebarLinks role={user?.role} onNavigate={() => setMobileOpen(false)} />
      </div>
      <div className="shrink-0 px-5 pt-6 text-[11px] text-slate-500">
        Dados que orientam • Ações que transformam
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden w-[260px] shrink-0 lg:block">
        <div className="fixed h-screen w-[260px]">{Sidebar}</div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden" data-testid="mobile-menu-button"><Menu className="h-6 w-6 text-slate-700" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] border-0 p-0"><SheetTitle className="sr-only">Menu de navegação</SheetTitle>{Sidebar}</SheetContent>
          </Sheet>

          <div className="hidden flex-1 sm:block">{user?.role !== "COZINHEIRA" && <GlobalSearch />}</div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {isSuperadmin && (
              <Select value={selectedSchoolId || "all"} onValueChange={(v) => chooseSchool(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-[150px] text-xs sm:w-[220px]" data-testid="school-selector">
                  <Building2 className="mr-1 h-4 w-4 text-slate-400" />
                  <SelectValue placeholder="Escola" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as escolas</SelectItem>
                  {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {!isSuperadmin && schools[0] && (
              <span className="hidden max-w-[200px] truncate rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 md:inline">
                {schools[0].name}
              </span>
            )}

            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <Avatar className="h-9 w-9 border border-slate-200">
                    <AvatarFallback className="bg-blue-700 text-sm font-semibold text-white">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold text-slate-800">{user?.name}</div>
                  <div className="text-xs font-normal text-slate-400">{ROLE_LABELS[user?.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Mobile search row */}
        {user?.role !== "COZINHEIRA" && <div className="border-b border-slate-200 bg-white px-4 py-2 sm:hidden"><GlobalSearch /></div>}

        <main className="flex-1 px-4 pb-24 pt-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl"><Outlet /></div>
        </main>
        <AcompanhaIA />
      </div>
    </div>
  );
}
